import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { RowsPhotoAlbum } from "react-photo-album";
import "react-photo-album/rows.css";
import { api } from "../lib/api";
import { formatDate } from "../lib/format";

interface GallerySettings {
  spacing: number;
  updated_at: string | null;
  updated_by: string | null;
}

interface PreviewPhoto {
  uuid: string;
  thumb_url: string;
  width: number;
  height: number;
  title: string;
  is_active: number;
  purged_at: string | null;
}

// Must match the API's GALLERY_SPACING_MAX.
const SPACING_MAX = 64;
// Mirrors the storefront: <Gallery> uses targetRowHeight 400 inside a
// max-w-7xl (1280px) container with 16px side padding; phones are ~375px wide.
const TARGET_ROW_HEIGHT = 400;
const VIEWPORTS = { desktop: 1280 - 32, phone: 375 - 32 } as const;
const PREVIEW_PHOTOS = 12;

export function GallerySpacing() {
  const [saved, setSaved] = useState<GallerySettings | null>(null);
  const [spacing, setSpacing] = useState(20);
  const [photos, setPhotos] = useState<PreviewPhoto[]>([]);
  const [viewport, setViewport] = useState<keyof typeof VIEWPORTS>("desktop");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{
    text: string;
    error: boolean;
  } | null>(null);

  useEffect(() => {
    api
      .get<{ settings: GallerySettings }>("/api/admin/settings/gallery")
      .then((d) => {
        setSaved(d.settings);
        setSpacing(d.settings.spacing);
      })
      .catch((err) => setMessage({ text: err.message, error: true }));
    api
      .get<{ photos: PreviewPhoto[] }>("/api/admin/photos")
      .then((d) =>
        setPhotos(
          d.photos
            .filter((p) => p.is_active && !p.purged_at)
            .slice(0, PREVIEW_PHOTOS)
        )
      )
      .catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const d = await api.put<{ settings: GallerySettings }>(
        "/api/admin/settings/gallery",
        { spacing }
      );
      setSaved(d.settings);
      setSpacing(d.settings.spacing);
      setMessage({
        text: "Saved. Live on the storefront on the next page load.",
        error: false,
      });
    } catch (err) {
      setMessage({
        text: err instanceof Error ? err.message : "Save failed",
        error: true,
      });
    } finally {
      setBusy(false);
    }
  };

  if (!saved)
    return <section style={card}>{message?.text ?? "Loading…"}</section>;

  const dirty = spacing !== saved.spacing;

  return (
    <section style={card}>
      <h2 style={h2}>Gallery</h2>
      <p style={muted}>The space between photos in the storefront gallery.</p>

      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginTop: 16,
          fontSize: 14,
        }}
      >
        <span style={{ fontWeight: 500 }}>Spacing</span>
        <input
          type="range"
          min={0}
          max={SPACING_MAX}
          step={1}
          value={spacing}
          onChange={(e) => setSpacing(Number(e.target.value))}
          style={{ flex: 1 }}
        />
        <span
          style={{
            width: 44,
            textAlign: "right",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {spacing}px
        </span>
      </label>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: 16,
        }}
      >
        <span style={{ fontWeight: 500, fontSize: 14 }}>Preview</span>
        <div style={{ display: "flex", gap: 4 }}>
          {(Object.keys(VIEWPORTS) as (keyof typeof VIEWPORTS)[]).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setViewport(v)}
              style={{ ...toggle, ...(viewport === v ? toggleOn : null) }}
            >
              {v === "desktop" ? "Desktop" : "Phone"}
            </button>
          ))}
        </div>
      </div>
      {photos.length === 0 ? (
        <p style={muted}>No active photos to preview.</p>
      ) : (
        <ScaledAlbum
          photos={photos}
          spacing={spacing}
          width={VIEWPORTS[viewport]}
        />
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginTop: 20,
        }}
      >
        <button
          type="button"
          onClick={save}
          disabled={busy || !dirty}
          style={{ ...btnPrimary, opacity: dirty ? 1 : 0.5 }}
        >
          {busy ? "Saving…" : "Save"}
        </button>
        {dirty && (
          <button
            type="button"
            onClick={() => setSpacing(saved.spacing)}
            style={btnSecondary}
          >
            Reset to {saved.spacing}px
          </button>
        )}
        {message && (
          <span
            style={{
              fontSize: 13,
              color: message.error ? "#b91c1c" : "#15803d",
            }}
          >
            {message.text}
          </span>
        )}
        {saved.updated_at && !message && !dirty && (
          <span style={muted}>
            Last changed {formatDate(saved.updated_at)}
            {saved.updated_by ? ` by ${saved.updated_by}` : ""}
          </span>
        )}
      </div>
    </section>
  );
}

/**
 * Lays the album out at the storefront's real width, then scales it down to fit
 * the card, so rows break exactly as they will live and the gaps shrink in proportion.
 */
function ScaledAlbum({
  photos,
  spacing,
  width,
}: {
  photos: PreviewPhoto[];
  spacing: number;
  width: number;
}) {
  const outer = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    const measure = () => {
      if (!outer.current || !inner.current) return;
      const s = Math.min(1, outer.current.clientWidth / width);
      setScale(s);
      setHeight(inner.current.offsetHeight * s);
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (outer.current) ro.observe(outer.current);
    if (inner.current) ro.observe(inner.current);
    return () => ro.disconnect();
  }, [width]);

  return (
    <div style={{ marginTop: 8 }}>
      <div
        style={{
          maxHeight: 520,
          overflowY: "auto",
          border: "1px solid #e5e5e5",
          borderRadius: 6,
        }}
      >
        <div
          ref={outer}
          style={{ height, overflow: "hidden", background: "#fff" }}
        >
          <div
            ref={inner}
            style={{
              width,
              transform: `scale(${scale})`,
              transformOrigin: "top left",
              marginLeft: scale === 1 ? "auto" : undefined,
              marginRight: scale === 1 ? "auto" : undefined,
            }}
          >
            <RowsPhotoAlbum
              photos={photos.map((p) => ({
                key: p.uuid,
                src: p.thumb_url,
                width: p.width,
                height: p.height,
                alt: p.title,
              }))}
              targetRowHeight={TARGET_ROW_HEIGHT}
              spacing={spacing}
              defaultContainerWidth={width}
            />
          </div>
        </div>
      </div>
      <div style={{ ...muted, marginTop: 4 }}>
        {width + 32}px-wide screen
        {scale < 1 ? `, shown at ${Math.round(scale * 100)}%` : ""}
      </div>
    </div>
  );
}

const card: React.CSSProperties = {
  background: "#fff",
  border: "1px solid #e5e5e5",
  borderRadius: 8,
  padding: 24,
  marginTop: 16,
};
const h2: React.CSSProperties = {
  fontFamily: "Georgia, serif",
  fontSize: 20,
  fontWeight: 400,
  margin: 0,
};
const muted: React.CSSProperties = {
  fontSize: 12,
  color: "#6b7280",
  margin: "6px 0 0",
};
const toggle: React.CSSProperties = {
  padding: "4px 10px",
  background: "#fff",
  color: "#1a1a1a",
  border: "1px solid #e5e5e5",
  borderRadius: 6,
  fontSize: 12,
  cursor: "pointer",
};
const toggleOn: React.CSSProperties = {
  background: "#1a1a1a",
  color: "#fff",
  borderColor: "#1a1a1a",
};
const btnPrimary: React.CSSProperties = {
  padding: "10px 16px",
  background: "#1a1a1a",
  color: "#fff",
  border: "none",
  borderRadius: 6,
  fontSize: 14,
  cursor: "pointer",
};
const btnSecondary: React.CSSProperties = {
  padding: "10px 16px",
  background: "#fff",
  color: "#1a1a1a",
  border: "1px solid #e5e5e5",
  borderRadius: 6,
  fontSize: 14,
  cursor: "pointer",
};
